/* eslint-disable react-hooks/exhaustive-deps */
import {
  AppBar,
  Checkbox,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import i18n from 'i18n';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import Permission from 'components/ValidPermission';
import { useCheckboxes } from 'hooks';
import { PermissionsData } from 'modules/authenticate/interfaces';
import {
  getLeakedCredentialsUsersRequest,
  resetPasswordLeakedCredentialsRequest,
} from 'modules/partners/actions';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import {
  LeakedCredentialsUsersFields,
  LeakedCredentialsUsersProps,
} from 'modules/partners/interfaces';
import { cyrb53Hash, formatDateWithMoment } from 'utils/helpers';
import { ModalFields } from 'modules/modal/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import Button from 'components/Button';
import PageTitle from 'components/PageTitle';
import styles from './styles';
import ListClients from './ListClients';
import ListPartners from './ListPartners';
import CredentialPhaseStatus from './CredentialPhaseStatus';

const useStyles = makeStyles(styles);

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'reset_leaked_credentials_user_password',
];

const TAB_PENDING = 0;
const TAB_RESET = 1;

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
  password_reset: false,
};

interface Props {
  isLoading: boolean;
  leakedCredentialsUsers: LeakedCredentialsUsersProps;
  permissions?: PermissionsData;
  getLeakedCredentialsUsersRequest: (props) => void;
  resetPasswordLeakedCredentialsRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
}

const LeakedCredentials = ({
  isLoading,
  leakedCredentialsUsers,
  permissions,
  getLeakedCredentialsUsersRequest,
  resetPasswordLeakedCredentialsRequest,
  modalClose,
  modalOpen,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
    setQuery,
  } = useTableFilters(initialQuery);

  const activeTab = query.password_reset === true ? TAB_RESET : TAB_PENDING;

  const handleChangeTab = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setSearchValue('');
    setQuery(prev => ({
      ...prev,
      offset: 0,
      search: '',
      password_reset: newValue !== TAB_PENDING,
    }));
  };

  const { checks, handleCheck, isChecked, setChecks } = useCheckboxes<
    LeakedCredentialsUsersFields,
    number
  >({
    items: leakedCredentialsUsers?.results,
    isItemChecked: (item, check) => item?.id === check,
    createCheckItem: item => item?.id,
  });

  const results = leakedCredentialsUsers?.results ?? [];
  const pendingItems = results.filter(item => !item.password_reset);
  const pendingIdsFromResults = pendingItems.map(item => item.id);
  const isAllChecked =
    pendingIdsFromResults.length > 0 &&
    pendingIdsFromResults.every(id => isChecked(id));
  const handleHeaderCheckboxClick = () => {
    if (isAllChecked) {
      setChecks(prev => prev.filter(id => !pendingIdsFromResults.includes(id)));
    } else {
      setChecks(prev =>
        Array.from(new Set([...prev, ...pendingIdsFromResults]))
      );
    }
  };

  const pendingIds = checks.filter(id => {
    const item = results.find(r => r.id === id);
    return item && !item.password_reset;
  });
  const hasPendingSelected = pendingIds.length > 0;

  const headers = [
    {
      key: 'name',
      i18n: 'USER_KEY',
      order: true,
    },
    {
      key: 'email',
      i18n: 'EMAIL_KEY',
      order: true,
    },
    {
      key: 'partner_name',
      i18n: 'PARTNER_KEY',
      order: true,
    },
    {
      key: 'date',
      i18n: 'THREAT_WATCH_IDENTIFICATION_DATE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'last_access',
      i18n: 'LAST_LOGIN_DATE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'is_active',
      i18n: 'USER_STATUS_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'clients_count',
      i18n: 'CLIENTS_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'reset_status',
      i18n: 'LEAKED_CREDENTIALS_PHASE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'admin_notified',
      i18n: 'ADMIN_NOTIFIED_KEY',
      order: true,
      centerTitle: true,
      hidden: activeTab !== TAB_RESET,
    },
    {
      key: 'actions',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
      hidden:
        (!!permissions &&
          !permissions.reset_leaked_credentials_user_password) ||
        activeTab === TAB_RESET,
    },
  ].filter(item => !item.hidden);

  const getUsers = () => {
    getLeakedCredentialsUsersRequest({
      query,
    });
  };

  React.useEffect(() => {
    getUsers();
  }, [getLeakedCredentialsUsersRequest, query]);

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
        className={classes.titleMargin}
      >
        <PageTitle
          title={i18n.t('LEAKED_CREDENTIALS_KEY')}
          subtitle={i18n.t('LEAKED_CREDENTIALS_SUBTITLE_KEY')}
        />

        <Grid item>
          <RefreshButton
            id="btn-refresh"
            onClick={() => {
              handleSearch(searchValue);
            }}
            animate={isLoading}
            className={classes.refreshButton}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} className={clsx(classes.tabsWrapper, classes.rootBar)}>
        <AppBar position="static" color="default" className={classes.tabsBar}>
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              id="tab-leaked-pending"
              label={i18n.t('LEAKED_CREDENTIALS_TAB_PENDING_KEY')}
              className={clsx(classes.baseTabStyle, classes.selectedTab)}
            />
            <Tab
              id="tab-leaked-reset"
              label={i18n.t('LEAKED_CREDENTIALS_TAB_RESET_KEY')}
              className={clsx(classes.baseTabStyle, classes.selectedTab)}
            />
          </Tabs>
        </AppBar>
      </Grid>
      <Grid container justify="space-between" alignItems="center">
        <Grid item xs={6}>
          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.searchRow}
          >
            <SearchBar
              key={activeTab}
              id="search-credentials"
              isLoading={isLoading}
              searchValue={searchValue}
              placeholder={i18n.t('SEARCH_KEY')}
              handleSearch={handleSearch}
              setSearchValue={setSearchValue}
            />
          </Grid>
        </Grid>
        <Grid item>
          {activeTab === TAB_PENDING && (
            <Permission value={requiredActionsPermissions}>
              <Button
                outlineBlue
                startIcon={<RotateLeftIcon />}
                className={classes.resetButton}
                disabled={!hasPendingSelected}
                onClick={() =>
                  modalOpen({
                    title: i18n.t('FORCE_RESET_PASSWORD_KEY'),
                    content: i18n.t('CONFIRM_MULTIPLE_RESET_PASSWORD_KEY'),
                    footer: (
                      <Grid>
                        <Button
                          submitButton
                          id="submit-button"
                          onClick={() => {
                            resetPasswordLeakedCredentialsRequest({
                              ids: pendingIds,
                              query,
                            });
                            modalClose();
                          }}
                        >
                          {i18n.t('CONFIRM_KEY')}
                        </Button>
                        <Button
                          id="cancel-button"
                          variant="outlined"
                          onClick={modalClose}
                        >
                          {i18n.t('CANCEL_KEY')}
                        </Button>
                      </Grid>
                    ),
                    noBorder: true,
                    maxWidth: 'sm',
                  })
                }
              >
                {i18n.t('FORCE_RESET_SELECTED_KEY')}
              </Button>
            </Permission>
          )}
        </Grid>
      </Grid>
      <TableItems
        header={
          <>
            {activeTab === TAB_PENDING && (
              <Permission value={requiredActionsPermissions}>
                <TableCell className={classes.checkboxCell}>
                  <Checkbox
                    checked={isAllChecked}
                    onClick={handleHeaderCheckboxClick}
                  />
                </TableCell>
              </Permission>
            )}
            {headers.map(item => (
              <TableCell
                centerTitle={item?.centerTitle}
                key={item?.key}
                order={item?.order}
                markOderItem={itemOrder === item?.key}
                handleOrder={() =>
                  item?.order ? handleOrder(item?.key) : null
                }
              >
                {i18n.t(item?.i18n)}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: LeakedCredentialsUsersFields, index) => (
          <TableRow
            className={clsx(classes.summaryRowTable, {
              [classes.envRowDirect]:
                !!permissions && permissions.list_environment_instances,
            })}
            key={cyrb53Hash(index)}
          >
            {activeTab === TAB_PENDING && (
              <Permission value={requiredActionsPermissions}>
                <TableCell className={classes.checkboxCell}>
                  <Checkbox
                    disabled={item?.password_reset === true}
                    onClick={() => handleCheck(item?.id)}
                    checked={isChecked(item?.id)}
                  />
                </TableCell>
              </Permission>
            )}
            <TableCell copyable className={classes.textField}>
              {`${item?.name} ${item?.last_name}`}
            </TableCell>
            <TableCell copyable className={classes.textField}>
              {item?.email}
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <div className={classes.clientsCell}>
                <span>{item?.partners_count}</span>
                <Permission value="list_partner_dashboard">
                  <VisibilityOutlinedIcon
                    className={classes.eyeIcon}
                    onClick={() =>
                      modalOpen({
                        title: i18n.t('PARTNER_PAGE.LIST_PARTNER_KEY'),
                        content: <ListPartners userUuid={item?.usr_uuid} />,
                        noBorder: true,
                        maxWidth: 'lg',
                      })
                    }
                  />
                </Permission>
              </div>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <Typography className={classes.textFieldCenterText}>
                {formatDateWithMoment(item?.date)}
              </Typography>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <Typography className={classes.textFieldCenterText}>
                {formatDateWithMoment(item.last_access)}
              </Typography>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <Typography className={classes.textFieldCenterText}>
                {i18n.t(item?.is_active ? 'ACTIVE_KEY' : 'INACTIVE_KEY')}
              </Typography>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <div className={classes.clientsCell}>
                <span>{item?.clients_count}</span>
                <Permission value="list_leaked_credentials_clients">
                  <VisibilityOutlinedIcon
                    className={classes.eyeIcon}
                    onClick={() =>
                      modalOpen({
                        title: i18n.t('CLIENTS_KEY'),
                        content: <ListClients userUuid={item?.usr_uuid} />,
                        noBorder: true,
                        maxWidth: 'md',
                      })
                    }
                  />
                </Permission>
              </div>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <CredentialPhaseStatus
                status={item?.reset_status ?? 'pending'}
                password_reset_by_user_at={item?.password_reset_by_user_at}
              />
            </TableCell>
            {activeTab === TAB_RESET && (
              <TableCell className={classes.textFieldCenter}>
                <Typography className={classes.textFieldCenterText}>
                  {item?.admin_notified ? i18n.t('YES_KEY') : i18n.t('NO_KEY')}
                </Typography>
              </TableCell>
            )}

            {activeTab === TAB_PENDING && (
              <Permission value={requiredActionsPermissions}>
                <TableCell textAlignCenter>
                  <MenuActions id={`btn-options-leaked-users-${index}`}>
                    <MenuActionItem
                      id="btn-reset-user-password"
                      onClick={() =>
                        modalOpen({
                          title: i18n.t('FORCE_RESET_PASSWORD_KEY'),
                          content: i18n.t('CONFIRM_RESET_PASSWORD_KEY'),
                          footer: (
                            <Grid>
                              <Button
                                submitButton
                                id="submit-button"
                                onClick={() => {
                                  resetPasswordLeakedCredentialsRequest({
                                    ids: [item?.id],
                                    query,
                                  });
                                  modalClose();
                                }}
                              >
                                {i18n.t('CONFIRM_KEY')}
                              </Button>
                              <Button
                                id="cancel-button"
                                variant="outlined"
                                onClick={modalClose}
                              >
                                {i18n.t('CANCEL_KEY')}
                              </Button>
                            </Grid>
                          ),
                          noBorder: true,
                          maxWidth: 'sm',
                        })
                      }
                    >
                      <Typography className={classes.menuOptions}>
                        {i18n.t('FORCE_RESET_PASSWORD_KEY')}
                      </Typography>
                    </MenuActionItem>
                  </MenuActions>
                </TableCell>
              </Permission>
            )}
          </TableRow>
        )}
        data={leakedCredentialsUsers}
        isLoading={isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={i18n.t('LOGS_PAGE.NO_LOGS_KEY')}
      />
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  partners: { leakedCredentialsUsers, isLoadingLeakedCredentialsUsers },
}: ApplicationState) => ({
  permissions: data?.permissions,
  leakedCredentialsUsers,
  isLoading: isLoadingLeakedCredentialsUsers,
});

export default connect(mapStateToProps, {
  getLeakedCredentialsUsersRequest,
  resetPasswordLeakedCredentialsRequest,
  modalClose,
  modalOpen,
})(LeakedCredentials);
